import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { WorkOrderDto, StatusCodeDto } from '@app/generated/model';
import { WorkOrderObjectModel, WorkLineModel } from '@app/work-order/objects/work-order-object.model';
import { Store } from '@ngrx/store';
import { DeSelectObject, DeselectWorkLine, SelectObject, SelectWorkLine } from '@dwo/actions';
import { getSelectedObjects } from '@app/core/store/selectors/work-order.selector';
import { WorkOrderObjectService } from '@app/work-order/objects/work-order-object.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-object',
  templateUrl: './object.component.html',
  styleUrls: ['./object.component.scss'],
  standalone: false,
})
export class ObjectComponent implements OnInit {
  @Input() workOrder!: WorkOrderDto;
  @Input() parent?: WorkOrderObjectModel;
  @Input() object!: WorkOrderObjectModel;
  @Input() level = 0;
  @Input() showProgress = false;
  @Input() statusCodes: StatusCodeDto[] = [];
  @Input() objectList: WorkOrderObjectModel[] = [];

  @Output() selected = new EventEmitter<WorkOrderObjectModel>();

  public selectedChildren: WorkOrderObjectModel[] = [];
  public selectedReportObjectStatus?: StatusCodeDto;
  public reportProgress = 0;
  public maxLockedProgress = 0;

  public indentationPerStep = 18;
  public indentationStartingPoint = 20;

  public selectedObjects: WorkOrderObjectModel[] = [];
  public completionLink = '';
  public completionSystemName = '';

  constructor(
    private store: Store,
    public workOrderObjectService: WorkOrderObjectService,
  ) {}

  ngOnInit() {
    const objectExternalId = this.getObjectExternalId(this.object?.id || '');

    this.workOrderObjectService
      .getCompletionLink(this.workOrder?.projectId || '', objectExternalId, this.object?.objectNumber || '')
      .pipe(take(1))
      .subscribe((link) => {
        this.completionLink = link;
      });

    this.workOrderObjectService
      .getCompletionSystemName(this.workOrder?.projectId || '')
      .pipe(take(1))
      .subscribe((name) => {
        this.completionSystemName = name;
      });

    this.store.select(getSelectedObjects).subscribe((selectedObjects) => {
      if (selectedObjects.length > 0) {
        this.selectedObjects = selectedObjects;
      } else {
        this.selectedObjects = [];
      }
    });
  }

  public getDescription() {
    if (!this.object.objectStatusId) {
      return null;
    }

    const status = this.statusCodes.filter((sc) => sc.id === this.object.objectStatusId)[0];
    return `${status.number} - ${status.name}`;
  }

  public getProgressLabel(progress: number) {
    return `${Math.floor(progress).toFixed(0)} %`;
  }

  public getCompletionLink(objectId: string) {
    return this.completionLink;
  }

  public shouldShowCompletionLink(): boolean {
    return !!this.completionLink && this.object?.objectType !== 'Dummy Object';
  }

  private getObjectExternalId(objectId: string): string {
    const normalizedObjectId = objectId.toString().trim();
    if (!normalizedObjectId) {
      return '';
    }

    const objectPrefix = 'OB_';
    const prefixIndex = normalizedObjectId.indexOf(objectPrefix);

    if (prefixIndex < 0) {
      return normalizedObjectId;
    }

    const externalId = normalizedObjectId.substring(prefixIndex + objectPrefix.length).trim();
    return externalId || normalizedObjectId;
  }

  public toggleSelected() {
    if (!this.object.isSelected) {
      this.store.dispatch(new SelectObject(this.object));
      (this.object.workLines ?? []).forEach((wl) => {
        this.store.dispatch(new SelectWorkLine(wl));
      });
    } else {
      this.store.dispatch(new DeSelectObject(this.object));
      (this.object.workLines ?? []).forEach((wl) => {
        this.store.dispatch(new DeselectWorkLine(wl));
      });
    }
  }

  public canToggleSelection() {
    return !this.parent?.isSelected;
  }

  public toggleSelectedWorkLine(workLine: WorkLineModel) {
    if (!workLine.isSelected) {
      this.workOrderObjectService.addSelectedWorkLine(workLine);
    } else {
      this.workOrderObjectService.removeSelectedWorkLine(workLine);
    }
  }

  public canToggleWorkLineSelection(workLine: WorkLineModel) {
    return !this.parent?.isSelected && !this.object.isSelected && workLine.progress !== 100;
  }

  public expandSelected(e: Event) {
    e.stopPropagation();
    this.object.expandChildren = !this.object.expandChildren;
  }

  public getIndentation(isWorkLine = false): number {
    return (
      this.indentationStartingPoint + this.indentationPerStep * this.level + (isWorkLine ? this.indentationPerStep : 0)
    );
  }

  public getObjectNumberClass() {
    return [`object-level-${this.level}`, 'object-number'].join(' ');
  }
}
